import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShieldCheckIcon, BeakerIcon, LightBulbIcon, DocumentTextIcon, ArrowRightIcon, AcademicCapIcon,
    ChatBubbleLeftRightIcon, BookOpenIcon, ClipboardDocumentListIcon, CommandLineIcon, XMarkIcon, SparklesIcon
} from '@heroicons/react/24/outline';

// Data for the page sections
const coreFeatures = [
    { name: 'Master Manual Testing', description: 'Learn the fundamentals of writing test cases, executing tests, and reporting bugs effectively.', icon: DocumentTextIcon, link: '/tutorials' },
    { name: 'Dive into Automation', description: 'Get hands-on experience with industry-standard tools like Selenium and Cypress.', icon: BeakerIcon, link: '/automation-learning' },
    { name: 'Test Your Knowledge', description: 'Take our interactive quizzes to solidify your understanding and prepare for interviews.', icon: LightBulbIcon, link: '/quizzes' },
    { name: 'Explore Essential Tools', description: 'Discover the most popular tools in the QA industry, from Jira to Postman.', icon: ShieldCheckIcon, link: '/tools' }
];

// --- NEW: Data for the "What's New" Popup ---
const newFeatures = [
    { name: 'Interactive Resume Builder', description: 'Craft a professional resume with our new guided, step-by-step wizard.', icon: ClipboardDocumentListIcon, link: '/resume-helper' },
    { name: 'Automation Playground', description: 'Practice your automation scripts on a live page full of diverse web elements.', icon: CommandLineIcon, link: '/automation-practice' },
    { name: 'Comprehensive Glossary', description: 'A searchable A-Z dictionary for all essential QA terms and acronyms.', icon: BookOpenIcon, link: '/glossary' }
];


// --- Animation Variants ---
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } }};
const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100 } }};
const cardHover = { y: -5, boxShadow: "0px 20px 30px rgba(99, 102, 241, 0.2)", transition: { type: "spring", stiffness: 300 }};

// --- Main Page Component ---
const HomePage = () => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    // --- NEW: Logic to show the popup only once ---
    useEffect(() => {
        // The key in localStorage acts as a version. Change it for future updates.
        const updateKey = 'hasSeenUpdate_v1_CareerTools';
        const hasSeenUpdate = localStorage.getItem(updateKey);

        if (!hasSeenUpdate) {
            // Use a timeout to let the user see the page before showing the modal
            const timer = setTimeout(() => {
                setIsUpdateModalOpen(true);
            }, 1500); // 1.5 second delay

            return () => clearTimeout(timer);
        }
    }, []);

    const handleCloseModal = () => {
        const updateKey = 'hasSeenUpdate_v1_CareerTools';
        localStorage.setItem(updateKey, 'true');
        setIsUpdateModalOpen(false);
    };

    const handleMouseMove = (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    return (
        <> {/* Use a fragment to wrap the page and the modal */}
            <div className="space-y-28 overflow-hidden">
                {/* Hero Section */}
                <div
                    className="relative min-h-[60vh] flex items-center"
                    onMouseMove={handleMouseMove}
                >
                    <motion.div className="absolute inset-0 z-0 opacity-50" style={{ background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(129, 140, 248, 0.15), transparent 80%)` }} animate={{ background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(129, 140, 248, 0.15), transparent 80%)` }} transition={{ type: "tween", ease: "backOut", duration: 0.5 }} />
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center z-10">
                        <motion.div variants={containerVariants} initial="hidden" animate="visible">
                            <motion.h1 className="text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl md:text-7xl" variants={itemVariants}>Build Your Future in<span className="block bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent mt-2">Quality Assurance</span></motion.h1>
                            <motion.p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600" variants={itemVariants}>Your one-stop destination to understand the world of QA, from manual testing fundamentals to advanced automation techniques.</motion.p>
                            <motion.div className="mt-10 flex items-center gap-x-4" variants={itemVariants}>
                                <Link to="/getting-started" className="flex items-center gap-x-2 rounded-md bg-indigo-600 px-5 py-3 text-base font-semibold text-white shadow-lg hover:bg-indigo-500 transform hover:scale-105 transition-transform duration-300"><AcademicCapIcon className="h-5 w-5"/> View Roadmap</Link>
                                <Link to="/tutorials" className="flex items-center gap-x-2 text-base font-semibold leading-7 text-gray-900 hover:text-indigo-600 transition-colors">Start a Tutorial <ArrowRightIcon className="h-4 w-4" /></Link>
                            </motion.div>
                        </motion.div>
                        <motion.div className="hidden lg:block" variants={containerVariants} initial="hidden" animate="visible">
                            <div className="grid grid-cols-4 gap-4">
                                {coreFeatures.map((feature, i) => (
                                    <motion.div key={i} variants={itemVariants} className="bg-white p-4 rounded-xl shadow-lg border border-gray-100 flex flex-col items-center text-center">
                                        <div className="bg-indigo-50 p-3 rounded-full"><feature.icon className="h-8 w-8 text-indigo-500"/></div>
                                        <p className="text-sm font-semibold mt-3 text-gray-700">{feature.name}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Other page sections remain the same... */}
                <motion.div className="py-16" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={containerVariants}>
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <motion.div className="lg:text-center" variants={itemVariants}><h2 className="text-base font-semibold leading-7 text-indigo-600">Core Modules</h2><p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Everything You Need to Succeed</p><p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600 lg:mx-auto">We've structured our content to guide you from your very first step into QA to becoming a job-ready professional.</p></motion.div>
                        <motion.div className="mt-16" variants={containerVariants}>
                            <dl className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 text-center">
                                {coreFeatures.map((feature) => (<motion.div key={feature.name} variants={itemVariants} whileHover={cardHover}><Link to={feature.link} className="block p-8 bg-white rounded-xl shadow-lg h-full border border-gray-100"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50"><feature.icon className="h-8 w-8 text-indigo-600" aria-hidden="true" /></div><p className="mt-5 text-lg font-semibold leading-6 text-gray-900">{feature.name}</p><p className="mt-2 text-base leading-7 text-gray-600">{feature.description}</p></Link></motion.div>))}
                            </dl>
                        </motion.div>
                    </div>
                </motion.div>
                <motion.section className="bg-gray-50 py-20" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={containerVariants}>
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <motion.div className="lg:text-center" variants={itemVariants}><h2 className="text-base font-semibold leading-7 text-indigo-600">New Tools</h2><p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">From Learning to Landing a Job</p><p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600 lg:mx-auto">We've added powerful new tools to help you master QA terminology and craft the perfect resume.</p></motion.div>
                        <motion.div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8" variants={containerVariants}>
                            <motion.div variants={itemVariants} whileHover={cardHover}><Link to="/glossary" className="block p-8 bg-white rounded-xl shadow-lg h-full border border-gray-100 text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50"><BookOpenIcon className="h-8 w-8 text-indigo-600"/></div><h3 className="mt-5 text-lg font-semibold text-gray-900">Comprehensive Glossary</h3><p className="mt-2 text-base text-gray-600">A searchable A-Z dictionary of all the essential terms and acronyms in software testing.</p></Link></motion.div>
                            <motion.div variants={itemVariants} whileHover={cardHover}><Link to="/resume-helper" className="block p-8 bg-white rounded-xl shadow-lg h-full border border-gray-100 text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50"><ClipboardDocumentListIcon className="h-8 w-8 text-indigo-600"/></div><h3 className="mt-5 text-lg font-semibold text-gray-900">Interactive Resume Builder</h3><p className="mt-2 text-base text-gray-600">Craft a professional, job-ready resume with our guided, step-by-step wizard.</p></Link></motion.div>
                        </motion.div>
                    </div>
                </motion.section>
                {/* ... other sections ... */}

            </div>

            {/* --- NEW: Update Notification Modal --- */}
            <AnimatePresence>
                {isUpdateModalOpen && (
                    <motion.div
                        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
                            initial={{ scale: 0.9, y: -20, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.9, y: 20, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        >
                            <button onClick={handleCloseModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="w-6 h-6"/>
                            </button>
                            <div className="flex justify-center">
                                <div className="h-16 w-16 bg-indigo-100 rounded-full flex items-center justify-center">
                                    <SparklesIcon className="h-10 w-10 text-indigo-600"/>
                                </div>
                            </div>
                            <div className="text-center mt-4">
                                <h2 className="text-2xl font-bold text-gray-900">What's New at QA Hub!</h2>
                                <p className="mt-2 text-gray-600">We've added some powerful new tools to help you succeed.</p>
                            </div>
                            <ul className="mt-6 space-y-4">
                                {newFeatures.map((feature) => (
                                    <li key={feature.name} className="flex items-start space-x-4">
                                        <div className="flex-shrink-0 h-8 w-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                                            <feature.icon className="h-5 w-5 text-indigo-500"/>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800">{feature.name}</h3>
                                            <p className="text-sm text-gray-500">{feature.description}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={handleCloseModal}
                                className="mt-8 w-full bg-indigo-600 text-white font-semibold py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                Explore Now
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default HomePage;